import { SegmentedControl } from '@/components/ui/workspace';
import { DOCUMENT_PARTIES, DOCUMENT_PARTY_LABEL, type DocumentParty } from '@/lib/api/documents';
import { cn } from '@/lib/utils/cn';

/**
 * "Whose document is this?" — the one question asked at upload (E19 / T-0215,
 * design §2): Vendor (the default) · Avero · Client, as a segmented choice
 * with one line of help. The upload modal asks it once per batch, the
 * new-engagement wizard's upload step asks it the same way, and a lead
 * changes an existing document's party through it on the document detail.
 *
 * The segmented control is the shared `.seg` primitive: a radiogroup, so
 * Tab reaches it and the arrow keys move the choice.
 */

export const PARTY_QUESTION = 'Whose document is this?';
export const PARTY_HELP =
  "Vendor: the implementer's or software vendor's contracts and reports. Avero: our IV&V contract, proposal and reports. Client: the client's own material.";

const OPTIONS = DOCUMENT_PARTIES.map((party) => ({
  value: party,
  label: DOCUMENT_PARTY_LABEL[party],
}));

export interface PartyChoiceProps {
  value: DocumentParty;
  onChange: (party: DocumentParty) => void;
  /** Show the choice without allowing a change (while a save is in flight). */
  disabled?: boolean;
  className?: string;
}

export function PartyChoice({ value, onChange, disabled = false, className }: PartyChoiceProps) {
  return (
    <div className={cn('flex flex-col items-start gap-1.5', className)}>
      <span className="text-meta font-medium text-ink-700">{PARTY_QUESTION}</span>
      <SegmentedControl
        aria-label={PARTY_QUESTION}
        options={OPTIONS}
        value={value}
        onChange={onChange}
        readOnly={disabled}
      />
      <p className="m-0 text-meta text-ink-500">{PARTY_HELP}</p>
    </div>
  );
}
